import { format } from "date-and-time";
import { toast } from "react-toastify";

import { v4 as uuidv4 } from "uuid";

export const isIterableArray = (array) =>
  Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

//===============================
// Cookie
//===============================
export const getCookieValue = (name) => {
  const value = document.cookie.match(
    "(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)"
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + "M"
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + "K"
    : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);
export const getSubtotal = (items) =>
  items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) =>
  total - total * (discount / 100);

export const getProductsQuantity = (products) =>
  products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str = "") => {
  if (str !== "")
    return (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, " ");
  else return "";
};

export const camelize = (str) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = (str) => {
  return str.toLowerCase().replaceAll(" ", "-");
};

//routes helper

export const flatRoutes = (childrens) => {
  const allChilds = [];

  const flatChild = (childrens) => {
    childrens.forEach((child) => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

export const getFlatRoutes = (children) =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name)]: flatRoutes(val.children),
        };
      } else {
        return {
          ...acc,
          unTitled: [...acc.unTitled, val],
        };
      }
    },
    { unTitled: [] }
  );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const copyToClipBoard = (textFieldRef) => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand("copy");
};

export const reactBootstrapDocsUrl = "https://react-bootstrap.github.io";

export const pagination = (currentPage, size) => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const addIdField = (items) => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));
};

// get file size

export const getSize = (size) => {
  if (size < 1024) {
    return `${size} Byte`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (
  startDate,
  endDate,
  interval = 1000 * 60 * 60 * 24
) => {
  const duration = endDate - startDate;
  const steps = duration / interval;
  return Array.from(
    { length: steps + 1 },
    (v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

/* Get Past Dates */
export const getPastDates = (duration) => {
  let days;

  switch (duration) {
    case "week":
      days = 7;
      break;
    case "month":
      days = 30;
      break;
    case "year":
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = (items) =>
  items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));

// Get Percentage
export const getPercentage = (number, percent) => {
  return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

export const formatLogInfo = (info, noTime = false) => {
  // console.log(noTime);
  // console.log(info);
  info = new Date(info);
  if (noTime) {
    info = format(info, "MMM DD, YYYY");
  } else {
    info = format(info, "MMM DD, YYYY - hh:mm A");
  }
  info = info === undefined ? "-" : info;
  return info;
};

// Slugify text
export const slugifyText = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

export const formatToCurrency = (amount) => {
  return "$" + amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

export const getFormattedSentence = (data = "") => {
  if (data?.length) {
    if (data?.length > 60) return data?.slice(0, 15) + "...";
    else return data;
  } else {
    return "-";
  }
};

export const b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const wait = (milliseconds) => {
  const date = Date.now();
  let currentDate = null;
  do {
    currentDate = Date.now();
  } while (currentDate - date < milliseconds);
};

export const addDays = (date, days) => {
  let dateNew = getPureDate(false, date);
  const copy = getPureDate(false, Number(date));
  copy.setDate(dateNew.getDate() + days);
  return copy;
};

export const getDaysFromDatesRange = (start, end, includesLastDay = true) => {
  let days = (end - start) / (1000 * 3600 * 24);
  days = Math.ceil(days);
  if (includesLastDay) days++; // includes last day
  // console.log("days from dates range=" + days);
  return days;
};

export const getBase64Type = (base64String = "") => {
  let logoType = "";
  if (base64String?.startsWith("http")) logoType = "link";
  if (base64String?.startsWith("/9g")) logoType = "jpg";
  if (base64String?.startsWith("/9j")) logoType = "jpeg";
  if (base64String?.startsWith("iVBORw0KGgo=")) logoType = "png";
  return logoType;
};

export const getTodayItemsFromList = (
  list = [],
  isCustomDate = false,
  customDate
) => {
  let todayData = [];
  // console.log(customDate);
  let today = new Date().toDateString();

  todayData = list?.filter((v) => {
    let permitDate = new Date(v.start_date).toDateString();
    // console.log(permitDate);

    if (isCustomDate) return permitDate === customDate.toDateString();
    else return permitDate === today;
  });
  // console.log(todayData);
  return todayData;
};

export const getDaysInMonth = (date) => {
  let d = new Date(date);
  d.setHours(0, 0, 0, 0);
  let y = d.getFullYear();
  let m = d.getMonth();
  return new Date(parseInt(y), parseInt(m) + 1, 0).getDate();
};

export const getPureDate = (today = true, date, last_midnight = true) => {
  if (today && last_midnight) {
    // default date obj with time 0
    return new Date(new Date().setHours(0, 0, 0, 0));
  }
  if (!today && last_midnight) {
    // given date obj with time 0
    return new Date(new Date(date).setHours(0, 0, 0, 0));
  }
  if (today && !last_midnight) {
    // default date obj with time 0 future midnight
    return new Date(new Date().setHours(0, 0, 0, 0));
  }
  if (!today && !last_midnight) {
    // given date obj with time 0 future midnight
    return new Date(new Date(date).setHours(0, 0, 0, 0));
  }
};

export const isDateOfCurrentYearAndMonth = (date1, date2) => {
  date1 = getPureDate(false, date1);
  date2 = getPureDate(false, date2);
  if (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth()
  ) {
    // console.log("date is same & true");
    return true;
  } else {
    // console.log("date is not same & true");
    return false;
  }
};

export function highlightMatch(text, matchString) {
  let textArr = text.split(" ");
  let returnArr = [];

  for (let i = 0; i < textArr.length; i++) {
    let subStrMatch = textArr[i]
      .toLowerCase()
      .indexOf(matchString.toLowerCase());

    if (subStrMatch !== -1) {
      let subStr = textArr[i].split("");
      let subStrReturn = [];

      for (let j = 0; j < subStr.length; j++) {
        if (j === subStrMatch) {
          subStrReturn.push("<strong>" + subStr[j]);
        } else if (j === subStrMatch + (matchString.length - 1)) {
          subStrReturn.push(subStr[j] + "<strong>");
        } else {
          subStrReturn.push(subStr[j]);
        }
      }
      returnArr.push(subStrReturn.join(""));
    } else {
      returnArr.push(textArr[i]);
    }
  }
  return returnArr;
}

export function byteConverter(bytes, decimals, only, smartReply = false) {
  const K_UNIT = 1024;
  const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

  if (bytes == 0) return "0 Byte";

  if (only === "MB" && !smartReply)
    return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals);

  if (only === "MB" && smartReply) {
    if ((bytes / (K_UNIT * K_UNIT)).toFixed(decimals) > 0)
      return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals);
    else return "< 0";
  }

  let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  let resp =
    parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) +
    " " +
    SIZES[i];

  return resp;
}

export function timeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();

  const seconds = Math.floor((now - date) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years > 0) {
    return years === 1 ? "1 year ago" : `${years} years ago`;
  } else if (months > 0) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else {
    return "Just now";
  }
}

export function sortByLatest(objectsArray, dateProperty) {
  if (!Array.isArray(objectsArray) || objectsArray.length === 0) {
    return [];
  }

  if (!dateProperty || typeof dateProperty !== "string") {
    return objectsArray;
  }

  return objectsArray.sort(
    (a, b) => new Date(b[dateProperty]) - new Date(a[dateProperty])
  );
}

export const getHostNameOld = () => {
  let host = "http://localhost:5015";

  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    // dev code
    host = process.env.REACT_APP_SERVER_LOCAL;
  } else {
    // production code
    host = process.env.REACT_APP_SERVER_LIVE;
  }

  // console.log(host);
  return host;
};

const normalizeHostUrl = (value, fallback, defaultScheme = "https://") => {
  const trimmed = (value || "").trim().replace(/\/+$/, "");

  if (!trimmed) return fallback;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(trimmed)) {
    return `http://${trimmed}`;
  }

  return `${defaultScheme}${trimmed}`;
};

export const getHostName = () => {
  const host = (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
    ? normalizeHostUrl(
        process.env.REACT_APP_SERVER_LOCAL,
        "http://localhost:5015",
        "http://"
      )
    : normalizeHostUrl(
        process.env.REACT_APP_SERVER_LIVE,
        "https://api-auraskinpremium.bin-mohammad-tech.com",
        "https://"
      );

  if (typeof window !== "undefined" && !window.__AURASKIN_HOST_LOGGED__) {
    window.__AURASKIN_HOST_LOGGED__ = true;
    console.info("[Auraskin] API server URL resolved:", host);
    console.info("[Auraskin] NODE_ENV:", process.env.NODE_ENV || "development");
  }

  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    // Dev mode: prefer environment variable, fallback to localhost
    return host;
  } else {
    // Production: prefer environment variable, fallback to known live host
    return host;
  }
};


export const getHostRole = () => {
  let host = "http://localhost:5015";

  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    // dev code
    host = normalizeHostUrl(
      process.env.REACT_APP_SERVER_LOCAL,
      "http://localhost:5015",
      "http://"
    );
  } else {
    // production code
    host = normalizeHostUrl(
      process.env.REACT_APP_SERVER_LIVE,
      "https://api-auraskinpremium.bin-mohammad-tech.com",
      "https://"
    );
  }

  // console.log(host);
  return host;
};

export function convertUppercaseToLowercase(input) {
  // Create a new string with only the letters converted to lowercase
  return input.replace(/[A-Z]/g, (match) => match.toLowerCase());
}

export const getAnonUserId = () => {
  let anonId = localStorage.getItem("anonUserId");
  if (!anonId) {
    anonId = uuidv4();
    localStorage.setItem("anonUserId", anonId);
  }
  return anonId;
};

/**
 * Format a number as PKR currency
 * @param {number} amount - The amount to format
 * @param {boolean} withDecimals - Show decimals (true/false)
 * @returns {string} - e.g. "₨ 25,000" or "₨ 25,000.50"
 */
export function formatPKR(amount, withDecimals = true) {
  if (isNaN(amount)) return "₨ 0";

  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: withDecimals ? 2 : 0,
  }).format(amount);
}

export function handleCopyToCB(textToCopy = "text copid") {
  try {
    navigator.clipboard.writeText(textToCopy);
    toast.success("Copied to clipboard.");
  } catch (err) {
    console.log(err);
    toast.error("Something went wrong!");
  }
}

export function formatDate(date, withTime = false) {
  if (!date) return "—";

  const options = {
    year: "numeric",
    month: "short", // Jan, Feb, Mar...
    day: "2-digit",
  };

  if (withTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.second = "2-digit";
  }

  return new Date(date).toLocaleString("en-US", options);
}
